import type { Child } from "@spork/s4p-server-sdk";
import { View } from "react-native";

import { ChildSelector } from "@/components/ChildSelector";
import { Label } from "@/components/ui/label";
import { Text } from "@/components/ui/text";

import { useTranslation } from "react-i18next";
import { joinChildLabels } from "./getChildLabel";
import { toggleSelectedChildId } from "./toggleSelectedChildId";

type ActionChildSelectorBase = {
    childList: Child[];
    isLoading?: boolean;
    disabled?: boolean;
};

type ActionChildSelectorProps = ActionChildSelectorBase &
    (
        | {
              multiple?: false;
              selectedChildId: number | null;
              onSelectChild: (childId: number) => void;
          }
        | {
              multiple: true;
              selectedChildIds: number[];
              onChangeSelectedChildIds: (childIds: number[]) => void;
          }
    );

export function ActionChildSelector(props: ActionChildSelectorProps) {
    const { t } = useTranslation();
    const {
        childList,
        isLoading = false,
        disabled = false,
    } = props;

    if (childList.length === 0) {
        return <Text variant="muted">{t("leave.student.noStudents")}</Text>;
    }

    // Single child is auto-selected on the parent screen — no label needed.
    if (childList.length === 1) {
        return null;
    }

    const selectedChildIds = props.multiple
        ? props.selectedChildIds
        : props.selectedChildId != null
          ? [props.selectedChildId]
          : [];

    if (disabled) {
        const selectedChildren =
            selectedChildIds.length === 0
                ? childList
                : childList.filter((child) =>
                      selectedChildIds.includes(child.id),
                  );

        return (
            <View className="gap-1">
                <Text variant="caption">
                    {props.multiple
                        ? t("leave.student.labelMultiple")
                        : t("leave.student.label")}
                </Text>
                <Text>{joinChildLabels(selectedChildren)}</Text>
            </View>
        );
    }

    function handleSelectChild(childId: number) {
        if (props.multiple) {
            props.onChangeSelectedChildIds(
                toggleSelectedChildId(props.selectedChildIds, childId),
            );
            return;
        }

        props.onSelectChild(childId);
    }

    return (
        <View className="gap-2">
            <Label>
                {props.multiple
                    ? t("leave.student.labelMultiple")
                    : t("leave.student.label")}
            </Label>
            <ChildSelector
                childList={childList}
                selectedChildIds={selectedChildIds}
                isLoading={isLoading}
                onSelectChild={handleSelectChild}
                disabled={disabled}
            />
        </View>
    );
}
